import { doc, getDoc } from 'firebase/firestore';
import { db } from '../constants/firebase';
import { createLogger } from './logger';

const logger = createLogger('cartHelpers');

/**
 * Get cart data for a user from Firestore
 */
export const getCartData = async (userId: string, supermarketId: string) => {
  try {
    const cartRef = doc(db, 'Carts', userId);
    const cartDoc = await getDoc(cartRef);
    
    if (cartDoc.exists()) {
      const cartData = cartDoc.data();
      return cartData[supermarketId] || {};
    }
    
    return {};
  } catch (error) {
    logger.error('Error fetching cart data:', error);
    return {};
  }
};

/**
 * Get active cart instance ID
 */
export const getActiveCartInstanceId = (cartData: any): string | null => {
  if (!cartData || typeof cartData !== 'object') {
    return null;
  }
  
  // Find active cart instance
  for (const instanceId in cartData) {
    if (instanceId === 'lastGlobalUpdate') continue;
    
    const instance = cartData[instanceId];
    if (instance && instance.status === 'active') {
      return instanceId;
    }
  }
  
  return null;
};

/**
 * Format cart items for notification display
 */
export const formatCartItems = (cartData: any): Array<{ name: string; quantity: number; price: number }> => {
  const itemsMap: Map<string, { name: string; quantity: number; price: number }> = new Map();
  
  if (!cartData || typeof cartData !== 'object') {
    return [];
  }
  
  // Iterate through cart instances
  for (const instanceId in cartData) {
    if (instanceId === 'lastGlobalUpdate' || typeof cartData[instanceId] !== 'object') continue;
    
    const instance = cartData[instanceId];
    if (instance && instance.products && Array.isArray(instance.products)) {
      instance.products.forEach((product: any) => {
        // Only include items with quantity > 0
        if (product.quantity && product.quantity > 0) {
          const itemKey = `${product.name}_${product.price}`;
          if (itemsMap.has(itemKey)) {
            const existing = itemsMap.get(itemKey)!;
            existing.quantity += product.quantity;
          } else {
            itemsMap.set(itemKey, {
              name: product.name || 'Unknown Product',
              quantity: product.quantity,
              price: product.price || 0
            });
          }
        }
      });
    }
  }
  
  return Array.from(itemsMap.values());
};

/**
 * Calculate total items in cart
 */
export const calculateTotalItems = (cartData: any): number => {
  let total = 0;
  
  if (!cartData || typeof cartData !== 'object') {
    return total;
  }
  
  // Helper function to add product quantity
  const addProductQuantity = (product: any) => {
    if (product.quantity && product.quantity > 0) {
      total += product.quantity;
    }
  };
  
  // Iterate through cart instances
  for (const instanceId in cartData) {
    if (instanceId === 'lastGlobalUpdate' || typeof cartData[instanceId] !== 'object') continue;
    
    const instance = cartData[instanceId];
    if (instance && instance.products && Array.isArray(instance.products)) {
      instance.products.forEach(addProductQuantity);
    }
  }
  
  return total;
};

/**
 * Calculate total price in cart
 */
export const calculateTotalPrice = (cartData: any): number => {
  let total = 0;
  
  if (!cartData || typeof cartData !== 'object') {
    return total;
  }
  
  // Helper function to add product price
  const addProductPrice = (product: any) => {
    if (product.quantity && product.quantity > 0) {
      total += product.quantity * (product.price || 0);
    }
  };
  
  // Iterate through cart instances
  for (const instanceId in cartData) {
    if (instanceId === 'lastGlobalUpdate' || typeof cartData[instanceId] !== 'object') continue;
    
    const instance = cartData[instanceId];
    if (instance && instance.products && Array.isArray(instance.products)) {
      instance.products.forEach(addProductPrice);
    }
  }
  
  return total;
};

/**
 * Get specific item quantity in cart
 */
export const getItemQuantity = (cartData: any, productId: string): number => {
  if (!cartData || typeof cartData !== 'object') {
    return 0;
  }
  
  // Iterate through cart instances
  for (const instanceId in cartData) {
    if (instanceId === 'lastGlobalUpdate' || typeof cartData[instanceId] !== 'object') continue;
    
    const instance = cartData[instanceId];
    if (instance && instance.products && Array.isArray(instance.products)) {
      const product = instance.products.find((p: any) => p.productId === productId);
      if (product) {
        return product.quantity || 0;
      }
    }
  }
  
  return 0;
};

/**
 * Get notification duration based on type and configuration
 */
export const getNotificationDuration = (type: string, customDuration?: number): number => {
  if (customDuration !== undefined) return customDuration;
  
  // Check localStorage for user preference
  const userPreference = localStorage.getItem('notification_duration');
  if (userPreference) return parseInt(userPreference);
  
  // Check environment variable
  if (type === 'cart' && process.env.REACT_APP_CART_NOTIFICATION_DURATION) {
    return parseInt(process.env.REACT_APP_CART_NOTIFICATION_DURATION);
  }
  
  // Default durations
  return type === 'cart' ? 6000 : 5000;
};

/**
 * Format currency for display
 */
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

/**
 * Show cart update notification for item removal or quantity changes
 */
export const showCartUpdateNotification = (
  showNotification: (notification: any) => void,
  navigate: (path: string) => void,
  action: 'removed' | 'updated',
  itemName: string,
  updatedCartData: any,
  supermarketId: string,
  supermarketName: string,
  userId: string
) => {
  // Validate cart data before showing notification
  if (!updatedCartData || typeof updatedCartData !== 'object') {
    logger.warn('Invalid cart data provided to notification');
    return;
  }

  // Check if there are any active items in the cart
  const cartItems = formatCartItems(updatedCartData);
  const totalItems = calculateTotalItems(updatedCartData);
  
  // Don't show notification if cart is empty
  if (cartItems.length === 0 || totalItems === 0) {
    logger.debug('Cart is empty, not showing notification');
    return;
  }

  const activeCartInstanceId = getActiveCartInstanceId(updatedCartData);
  
  // Determine the action text
  const actionText = action === 'removed' ? 'Item Removed from Cart' : 'Cart Updated';
  
  // Show enhanced notification
  showNotification({
    type: 'cart',
    title: actionText,
    message: '',
    duration: 6000, // 6 seconds
    cartData: {
      addedItem: {
        name: itemName,
        quantity: action === 'removed' ? 0 : 1, // Show 0 for removed items
        price: 0 // Price doesn't matter for removed items
      },
      cartItems: cartItems,
      totalItems: totalItems,
      totalPrice: calculateTotalPrice(updatedCartData),
      supermarketName: supermarketName
    },
    onViewCart: () => navigate('/carts'),
    onCheckout: () => {
      if (activeCartInstanceId) {
        navigate(`/checkout/${supermarketId}/${activeCartInstanceId}`);
      } else {
        navigate(`/checkout/${supermarketId}`);
      }
    },
    userId: userId,
    supermarketId: supermarketId
  });
};
